/**
 * Générateur — Il fait le lien entre stabilisation des fréquences et
 * probabilités.
 * Attendu ID : 217 | Niveau : 3ème | Domaine : Probabilités (d15, O5)
 *
 * Source doc prof (OGD FONCTIONS 3e, séance B2S1 Ex.11-13 — mutualisés banque
 * 5e et enrichis). Plan de cartes auto-validé 2026-07-16 (journal
 * TODO_EXERCISEUR_3E.md §6) :
 *
 *   « frequence »     — fréquences observées et cumulées (décimaux exacts)
 *   « truquee »       — la pièce est-elle truquée ? (QCM à règle EXPLICITE :
 *                       la réponse dépend du nombre de lancers ET de l'écart)
 *   « tableur »       — la formule de fréquence au tableur (QCM)
 *   « des »           — la somme de deux dés : P(somme = s) vs fréquence
 *   « composition »   — des fréquences stabilisées à la composition du sac (QCM)
 */

import {
    randomChoice, randInt, shuffle, tex,
    makeQcm, makeTexteTrou,
} from '../../../utils';

const ATTENDU_ID = 217;

const L_FREQUENCE = 'Calculer des fréquences observées';
const L_TRUQUEE = 'Fréquence observée : que peut-on conclure ?';
const L_TABLEUR = 'La formule de fréquence au tableur';
const L_DES = 'Somme de deux dés : probabilité et fréquence';
const L_COMPOSITION = 'Des fréquences stabilisées à la composition du sac';

const opts = (label) => ({ niveau: '3eme', matiere: 'maths', attendu: ATTENDU_ID, label });

const pgcd = (a, b) => { a = Math.abs(a); b = Math.abs(b); while (b) { [a, b] = [b, a % b]; } return a || 1; };
// Fraction dessinée à deux trous (cf. gen_216).
const trousFrac = (p, q) => { const g = pgcd(p, q); return `\\dfrac{{{${p / g}}}}{{{${q / g}}}}`; };

// Décimal exact de c/n pour n ∈ {20, 25, 40, 50, 100} (deux décimales max).
const decFr = (c, n) => {
    const cent = Math.round((c * 100) / n);                // exact : n divise 100·c
    if (cent % 100 === 0) return String(cent / 100);
    if (cent % 10 === 0) return `${Math.floor(cent / 100)},${(cent % 100) / 10}`;
    return `${Math.floor(cent / 100)},${String(cent % 100).padStart(2, '0')}`;
};

const PRENOMS = ['Pedro', 'Lucien', 'Léonard', 'Louis', 'Sergio', 'Anna', 'Mila', 'Karim'];

// ═════════ « frequence » — observée puis cumulée ═════════

const genFrequenceTrous = () => {
    const [p1, p2] = shuffle(PRENOMS).slice(0, 2);
    const n1 = randomChoice([20, 25, 50]);
    const c1 = randInt(Math.round(n1 * 0.4), Math.round(n1 * 0.75));
    let c2 = randInt(Math.round(n1 * 0.3), Math.round(n1 * 0.6));
    // Cumul EXACT à deux décimales : (c1+c2)·100 doit être divisible par 2n1.
    if (((c1 + c2) * 100) % (2 * n1) !== 0) c2 += 1;
    const f1 = decFr(c1, n1);
    const fCumul = decFr(c1 + c2, 2 * n1);
    const phrase = `$\\text{Fréquence de ${p1} : } {{${f1}}} \\qquad`
        + ` \\text{fréquence cumulée : } {{${fCumul}}}$`;
    const payload = makeTexteTrou(phrase,
        `<p>${p1} lance une pièce ${n1} fois et obtient ${c1} fois PILE. ${p2} la lance ensuite `
        + `${n1} fois et obtient ${c2} fois PILE.</p>`
        + `<p><b>Calcule la fréquence de PILE chez ${p1}, puis la fréquence cumulée sur les `
        + `${2 * n1} lancers.</b> Donne des écritures décimales exactes.</p>`,
        { ...opts(L_FREQUENCE), mode: 'saisie' });
    payload._meta.params = { n1, c1, c2, f1, fCumul };
    return payload;
};

// ═════════ « truquee » — que peut-on conclure ? (QCM à règle explicite) ═════════

const CH_PETIT = 'On ne peut rien conclure : le nombre de lancers est trop petit.';
const CH_OK = 'Rien n\'indique que la pièce est truquée : la fréquence stabilisée est proche de 0,5.';
const CH_TRUQUEE = 'La pièce est sans doute truquée : la fréquence stabilisée est loin de 0,5.';

const genTruqueeQcm = () => {
    const cas = randomChoice(['petit', 'ok', 'truquee']);
    let n; let cent;                                       // fréquence en centièmes
    if (cas === 'petit') { n = randomChoice([10, 20, 30, 50]); cent = randInt(55, 75); }
    else if (cas === 'ok') { n = randomChoice([1000, 2000, 5000]); cent = randInt(48, 52); }
    else { n = randomChoice([1000, 2000, 5000]); cent = randomChoice([randInt(58, 72), randInt(28, 42)]); }
    const f = cent % 10 === 0 ? `0,${cent / 10}` : `0,${cent}`;
    const correct = cas === 'petit' ? CH_PETIT : (cas === 'ok' ? CH_OK : CH_TRUQUEE);
    const payload = makeQcm([CH_PETIT, CH_OK, CH_TRUQUEE], correct,
        `<p>On lance une pièce ${n} fois : la fréquence de PILE vaut ${f}.</p>`
        + '<p>Règle de travail : on considère la fréquence STABILISÉE à partir de 1 000 lancers, '
        + 'et « proche de 0,5 » signifie entre 0,45 et 0,55.</p>'
        + '<p><b>Que peut-on conclure ?</b></p>',
        opts(L_TRUQUEE));
    payload._meta.params = { cas, n, cent };
    return payload;
};

// ═════════ « tableur » — formule de fréquence (QCM) ═════════

const genTableurQcm = () => {
    const ligne = randInt(2, 6);
    const colTotal = 'A'; const colEff = 'B'; const colFreq = 'C';
    const correct = `=${colEff}${ligne}/${colTotal}${ligne}`;
    const choix = [correct,
        `=${colTotal}${ligne}/${colEff}${ligne}`,
        `=${colEff}${ligne}*${colTotal}${ligne}`,
        `=${colEff}${ligne}/100`];
    const couleur = randomChoice(['rouge', 'vert', 'bleu', 'jaune']);
    const payload = makeQcm(choix, correct,
        `<p>Dans un tableur, la colonne ${colTotal} contient le nombre TOTAL de tirages, la colonne `
        + `${colEff} le nombre de jetons ${couleur}s obtenus. On veut la fréquence du ${couleur} `
        + `dans la colonne ${colFreq}.</p>`
        + `<p><b>Quelle formule faut-il saisir en ${colFreq}${ligne} ?</b></p>`,
        opts(L_TABLEUR));
    payload._meta.params = { ligne, couleur };
    return payload;
};

// ═════════ « des » — P(somme = s) vs fréquence observée ═════════

const genDesTrous = () => {
    const s = randInt(4, 10);
    const fav = 6 - Math.abs(7 - s);
    const freqObs = randInt(Math.max(1, Math.round(fav / 36 * 100) - 5), Math.round(fav / 36 * 100) + 5);
    const phrase = `$\\text{Cases favorables : } {{${fav}}} \\qquad`
        + ` P(\\text{somme} = ${s}) = ${trousFrac(fav, 36)}$`;
    const payload = makeTexteTrou(phrase,
        `<p>On lance deux dés équilibrés et on additionne. Il y a ${tex('6 \\times 6 = 36')} issues `
        + 'possibles, les couples (dé 1 ; dé 2). Une simulation de 1 000 lancers a donné une fréquence '
        + `d'environ ${freqObs} % pour la somme ${s}.</p>`
        // « plus on lance, plus elles se rapprochent » énonçait la loi des
        // grands nombres, que la comparaison demandée fait justement observer.
        + `<p><b>Compte les cases donnant la somme ${s}, puis calcule ${tex(`P(\\text{somme} = ${s})`)}.</b> `
        + 'Donne une fraction irréductible, puis compare avec la fréquence observée.</p>',
        { ...opts(L_DES), mode: 'saisie' });
    payload._meta.params = { s, fav, freqObs };
    return payload;
};

// ═════════ « composition » — le raisonnement inverse (QCM) ═════════

const genCompositionQcm = () => {
    const T = randomChoice([20, 40]);
    // Composition en parts de 5 % : 4 couleurs, effectifs distincts.
    let parts;
    for (let essai = 0; essai < 60; essai++) {
        parts = [randInt(6, 10), randInt(3, 6), randInt(2, 5), 0];
        parts[3] = 20 - parts[0] - parts[1] - parts[2];
        if (parts[3] >= 1 && new Set(parts).size === 4 && (T * Math.min(...parts)) % 20 === 0) break;
        parts = null;
    }
    if (!parts) parts = [10, 5, 4, 1];                     // repli (doc Ex.12)
    const eff = parts.map(p => (T * p) / 20);
    const freqs = parts.map(p => decFr(p, 20));
    const couleurs = ['jaunes', 'verts', 'rouges', 'bleus'];
    const fmt = (t) => couleurs.map((c, i) => `${t[i]} ${c}`).join(', ');
    const correct = fmt(eff);
    const swap = [...eff]; [swap[1], swap[2]] = [swap[2], swap[1]];
    const double = eff.map(x => x * 2);
    const choix = [correct, fmt(swap), fmt(double)];
    const payload = makeQcm(choix, correct,
        `<p>Un sac contient ${T} jetons (jaunes, verts, rouges, bleus). Après 1 000 tirages avec `
        + 'remise, les fréquences se stabilisent vers : '
        + couleurs.map((c, i) => `${c} ${freqs[i]}`).join(' ; ') + '.</p>'
        + '<p><b>Quelle est la composition la plus probable du sac ?</b></p>',
        opts(L_COMPOSITION));
    payload._meta.params = { T, parts, eff };
    return payload;
};

// ─── Registre ────────────────────────────────────────────────
const GENERATORS = {
    frequence_texte_trou_saisie: genFrequenceTrous,
    truquee_qcm_unique: genTruqueeQcm,
    tableur_qcm_unique: genTableurQcm,
    des_texte_trou_saisie: genDesTrous,
    composition_qcm_unique: genCompositionQcm,
};

const FORMAT_LABELS = { texte_trou_saisie: 'Saisie trous', qcm_unique: 'QCM' };
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'frequence', label: L_FREQUENCE, formatsList: buildFormats('frequence') },
    { id: 'truquee', label: L_TRUQUEE, formatsList: buildFormats('truquee') },
    { id: 'tableur', label: L_TABLEUR, formatsList: buildFormats('tableur') },
    { id: 'des', label: L_DES, formatsList: buildFormats('des') },
    { id: 'composition', label: L_COMPOSITION, formatsList: buildFormats('composition') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
